/**
 * The bits of HTTP that more than one feature needs: a typed error, and the
 * CSRF token every editor write has to carry.
 *
 * Promoted out of the reader and the upload form once the translation editor
 * became the third caller — two copies of CSRF handling would eventually
 * disagree about when to clear a stale token.
 */

/** An error a component can render, with the cases it needs to distinguish. */
export class ApiError extends Error {
  readonly status: number;
  /** Stable code from the server's RFC 9457 body, when it sent one. */
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** The khutbah does not exist, or is not published. Deliberately the same case. */
  get notFound(): boolean {
    return this.status === 404;
  }

  /**
   * Not signed in. Worth its own test because a background `fetch` that gets
   * a 401 raises no browser credential prompt — without saying so, the editor
   * is told "the server returned 401" and has nothing to act on.
   */
  get unauthenticated(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Reads the server's Problem Details body for a message worth showing.
 *
 * Falls back to a generic line rather than surfacing raw text: an error body
 * is still untrusted input, and `detail` is only rendered because the server
 * writes those messages for people on purpose.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let detail: string | null = null;
  let code: string | null = null;
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null) {
      const problem = body as { detail?: unknown; code?: unknown };
      detail = typeof problem.detail === 'string' ? problem.detail : null;
      code = typeof problem.code === 'string' ? problem.code : null;
    }
  } catch {
    // No body, or not JSON. The status is all we have, and it is enough.
  }
  return new ApiError(
    detail ?? `The server returned ${response.status}.`,
    response.status,
    code,
  );
}

/**
 * The CSRF token, fetched once and reused.
 *
 * Spring Security defers token generation: the cookie is written only when
 * something actually *reads* the token, so an ordinary GET sets nothing and a
 * SPA that assumed otherwise finds its first write refused with no obvious
 * cause. Asking the endpoint is what materialises it.
 */
let cached: { headerName: string; token: string } | null = null;

export async function csrfHeaders(
  signal: AbortSignal,
): Promise<Record<string, string>> {
  if (cached === null) {
    const response = await fetch('/api/public/csrf', {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw await toApiError(response);
    }
    const body: unknown = await response.json();
    const record = body as { headerName?: unknown; token?: unknown };
    if (typeof record.headerName !== 'string' || typeof record.token !== 'string') {
      throw new ApiError('The server did not issue a usable CSRF token.', 500, null);
    }
    cached = { headerName: record.headerName, token: record.token };
  }
  return { [cached.headerName]: cached.token };
}

/**
 * Forgets the token after a refusal.
 *
 * A token outlives neither a server restart nor a new session, and a cached
 * stale one would fail the same way forever — so every 401/403 drops it and
 * the next attempt fetches a fresh one.
 */
export function forgetCsrfToken() {
  cached = null;
}
